import { Navigate, Route, Routes, useNavigate } from "react-router";
import { useEffect, useState } from "react";

import { clearToken, getToken } from "./lib/auth";
import { isOrgAdmin, isPlatformAdmin } from "./lib/scopes";
import ErrorBoundary from "./components/ErrorBoundary";
import Sidebar from "./components/Sidebar";
import ConnectRoute from "./routes/ConnectRoute";
import OAuthCallbackRoute from "./routes/OAuthCallbackRoute";
import OverviewRoute from "./routes/OverviewRoute";
import SelfRoute from "./routes/SelfRoute";
import RealmsRoute from "./routes/RealmsRoute";
import PacksRoute from "./routes/PacksRoute";
import OrganizationsRoute from "./routes/OrganizationsRoute";
import ProvidersRoute from "./routes/ProvidersRoute";
import RecordsRoute from "./routes/RecordsRoute";

export default function App() {
  const [authed, setAuthed] = useState<boolean>(!!getToken());
  const nav = useNavigate();

  useEffect(() => {
    const onStorage = () => setAuthed(!!getToken());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  function logout() {
    clearToken();
    setAuthed(false);
    nav("/connect");
  }

  if (!authed) {
    return (
      <div className="min-h-screen">
        <header className="border-b border-kw-border-subtle px-6 py-3">
          <span className="text-kw-l3 font-mono text-sm">kw://cost</span>
        </header>
        <Routes>
          <Route
            path="/connect"
            element={<ConnectRoute onConnected={() => setAuthed(true)} />}
          />
          <Route
            path="/callback"
            element={<OAuthCallbackRoute onConnected={() => setAuthed(true)} />}
          />
          <Route path="*" element={<Navigate to="/connect" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-[220px_1fr]">
      <Sidebar onLogout={logout} />
      <main className="overflow-auto">
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="/overview" element={<OverviewRoute />} />
            <Route path="/me" element={<SelfRoute />} />
            <Route path="/realms" element={<RealmsRoute />} />
            <Route path="/packs" element={<PacksRoute />} />
            <Route
              path="/organizations"
              element={
                isOrgAdmin() ? (
                  <OrganizationsRoute />
                ) : (
                  <Navigate to="/overview" replace />
                )
              }
            />
            <Route
              path="/providers"
              element={
                isPlatformAdmin() ? (
                  <ProvidersRoute />
                ) : (
                  <Navigate to="/overview" replace />
                )
              }
            />
            <Route path="/records" element={<RecordsRoute />} />
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
  );
}
